/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Smartphone, Download, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const useInstallPrompt = () => {
  const [event, setEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    const onPrompt = (e) => {
      e.preventDefault();
      setEvent(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!event) return false;
    event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setEvent(null);
    return outcome === 'accepted';
  };

  return { canInstall: !!event, installed, promptInstall };
};

export const InstallAppCard = () => {
  const { t } = useTranslation();
  const { canInstall, installed, promptInstall } = useInstallPrompt();

  const handleInstall = async () => {
    const ok = await promptInstall();
    if (ok) toast.success(t('installAppInstalled'));
  };

  let button;
  if (installed) {
    button = (
      <Button variant="secondary" disabled className="gap-2" data-testid="settings-install-installed">
        <CheckCircle2 className="h-4 w-4 text-primary" /> {t('installAppInstalled')}
      </Button>
    );
  } else if (canInstall) {
    button = (
      <Button onClick={handleInstall} className="gap-2" data-testid="settings-install-btn">
        <Download className="h-4 w-4" /> {t('installAppAvailable')}
      </Button>
    );
  } else {
    button = (
      <div className="text-xs text-muted-foreground italic" data-testid="settings-install-unavailable">
        {t('installAppNotAvailable')}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border p-4 bg-secondary/30" data-testid="settings-install-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Smartphone className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{t('installApp')}</div>
          <p className="text-xs text-muted-foreground mt-1 mb-3">{t('installAppDesc')}</p>
          {button}
        </div>
      </div>
    </div>
  );
};
